import { useState } from 'react';
import { useT } from '@hana/i18n';
import { Button, Chip, Segmented, Table, useToast, type Column } from '@hana/ui';
import { LOT_FILTERS, LotView, type LotFilter } from './LotView';
import { BarsPanel, Card, Kpis, ListPanel, type Kpi } from './panels';
import { useList } from '@hana/data';
import { CIT, JOB, MON, MS, PI, PROF, TRF, VIN, VOUT, delta, f1, fi, moneyFrom, pc, sum } from './data';
import s from './bod.module.css';
import './i18nMoney';

// Mint-BOD · tab tiền (Tài chính · Thuế & VAT · Job lỗ): chỉ dựng sau quyền money (Bod.tsx lọc tab). Số liệu mẫu chép từ code board.
type Ix = readonly number[];
const CW = 720, CH = 180, SW = 60;

const mTile = (t: (k: string) => string, k: string, a: readonly number[], idx: Ix, pidx: Ix): Kpi => {
  const d = delta(sum(a, idx), sum(a, pidx));
  return { k, v: f1(sum(a, idx)), u: t('bod.m.unit'), sub: d[0].replace(' so kỳ trước', t('bod.vsPrev')), tone: d[1] === null ? undefined : d[1] ? 'ok' : 'err', sp: a };
};

/** Cột nhóm (tối đa 2 chuỗi) + đường tuỳ chọn; ngưỡng = đỉnh × 1,08 như board. */
function MChart({ title, sub, aria, series, line, legend, ymax, mx, idx }: { title: string; sub: string; aria: string; series: readonly (readonly number[])[]; line?: readonly number[]; legend: readonly (readonly [string, string])[]; ymax: string; mx: number; idx: Ix }) {
  const bw = (SW * 0.6) / series.length;
  const yy = (v: number) => CH - (Math.max(0, v) / mx) * (CH - 12);
  return (
    <Card title={title} sub={sub} span={8}>
      <div className={s.leg}>{legend.map(([k, c]) => <span key={k}><i className={s[`f${c}`]} />{k}</span>)}<em>{ymax}</em></div>
      <svg role="img" aria-label={aria} width="100%" height="180" viewBox={`0 0 ${CW} ${CH}`} preserveAspectRatio="none">
        <path d={idx.map((i) => `M${i * SW + 2} 0h${SW - 4}v${CH}h-${SW - 4}z`).join(' ')} className={s.hl} />
        <path d={[0.25, 0.5, 0.75, 1].map((q) => `M0 ${(CH - q * (CH - 12)).toFixed(1)}H${CW}`).join(' ') + ` M0 ${CH - 0.5}H${CW}`} className={s.gridL} vectorEffect="non-scaling-stroke" />
        {series.map((a, j) => <path key={legend[j]![0]} className={s[`f${legend[j]![1]}`]} d={a.map((v, i) => { const h = (Math.max(0, v) / mx) * (CH - 12); return h > 0.2 ? `M${(i * SW + SW * 0.2 + j * bw).toFixed(1)} ${yy(v).toFixed(1)}h${(bw - 2).toFixed(1)}v${h.toFixed(1)}h-${(bw - 2).toFixed(1)}z ` : ''; }).join('')} />)}
        {line && <path d={line.map((v, i) => `${i ? 'L' : 'M'}${(i * SW + SW / 2).toFixed(1)} ${yy(v).toFixed(1)}`).join(' ')} className={s.profL} vectorEffect="non-scaling-stroke" />}
      </svg>
      <div className={s.xl} aria-hidden="true">{MS.map((k, i) => <span key={k} className={idx.includes(i) ? s.xon : ''}>{k}</span>)}</div>
    </Card>
  );
}

type MRow = { key: string; i: number; cells: readonly string[]; neg?: boolean; warn?: boolean };

export function FinTab({ idx, pidx, onMonth }: { idx: Ix; pidx: Ix; onMonth: (i: number) => void }) {
  const t = useT();
  const bod = useList('bodMonth', { limit: 24 });
  const { REV, COST, PROF, DN, JDN } = moneyFrom(bod.data?.items);
  const toast = useToast();
  const R = sum(REV, idx), L = R - sum(COST, idx), tx = Math.max(0, L) * CIT;
  const dl = delta(L, sum(PROF, pidx));
  const mx = Math.max(...REV) * 1.08;
  const kpis: Kpi[] = [
    mTile(t, t('bod.m.revNet'), REV, idx, pidx), mTile(t, t('bod.m.cost'), COST, idx, pidx),
    { k: t('bod.m.profit'), v: f1(L), u: t('bod.m.unit'), sub: dl[0].replace(' so kỳ trước', t('bod.vsPrev')), tone: L < 0 ? 'err' : 'ok' },
    { k: t('bod.m.margin'), v: R ? pc((L / R) * 100) : '—', sub: t('bod.m.marginS') },
    { k: t('bod.m.cit'), v: f1(tx), u: t('bod.m.unit'), sub: t('bod.m.citS') },
    { k: t('bod.m.net'), v: f1(L - tx), u: t('bod.m.unit'), sub: t('bod.m.netS') },
  ];
  const cols: Column<MRow>[] = ([['month', 'c.month', '12%'], ['dn', 'c.dn', '11%'], ['jdn', 'c.jdn', '11%'], ['rev', 'c.rev', '14%'], ['cost', 'c.cost', '14%'], ['profit', 'c.profit', '14%'], ['margin', 'c.margin', '10%'], ['cit', 'c.cit', '14%']] as const).map(([key, h, width], c) => ({
    key, header: t(`bod.m.${h}`), width, align: c ? 'right' as const : undefined,
    cell: (r: MRow) => (c === 0 ? <b>{r.cells[0]}</b> : c === 5 ? <b className={r.neg ? s.down : undefined}>{r.cells[c]}</b> : r.cells[c]),
  }));
  const rows: MRow[] = MON.map((m, i) => ({ key: m, i, neg: PROF[i]! < 0, cells: [m, fi(DN[i]!), fi(JDN[i]!), f1(REV[i]!), f1(COST[i]!), f1(PROF[i]!), pc((PROF[i]! / REV[i]!) * 100), f1(Math.max(0, PROF[i]!) * CIT)] })).reverse();
  return (
    <div className={s.grid}>
      <Kpis items={kpis} />
      <MChart title={t('bod.m.chartFin')} sub={t('bod.m.byMonth')} idx={idx} mx={mx} series={[REV, COST]} line={PROF.map((v) => v * 4)} ymax={t('bod.m.peak', { v: f1(mx) })}
        aria={`${t('bod.m.chartFin')}: ${MS.map((m, i) => `${m} ${f1(REV[i]!)} / ${f1(PROF[i]!)}`).join(', ')}`}
        legend={[[t('bod.m.rev'), 'Ob'], [t('bod.m.cost'), 'Cost'], [t('bod.m.profit4'), 'Prof']]} />
      <div className={`${s.span4} ${s.stack}`}>
        <ListPanel title={t('bod.m.how')} span={4} rows={[
          [`${t('bod.m.rev')} · ${t('bod.m.how.rev')}`, t('bod.m.how.revS')],
          [`${t('bod.m.cost')} · ${t('bod.m.how.cost')}`, t('bod.m.how.costS')],
          [t('bod.m.how.trf'), f1(sum(TRF, idx) * 1.9)], [t('bod.m.how.int'), f1(sum(JOB, idx) * 0.42)], [t('bod.m.how.dep'), f1(sum(JOB, idx) * 0.65)],
        ]} />
        <Button variant="secondary" onClick={() => toast({ msg: t('bod.m.openedCosting') })}>{t('bod.m.openCosting')}</Button>
      </div>
      <Card title={t('bod.m.taxTbl')} span={12}>
        <Table label={t('bod.m.taxTbl')} columns={cols} rows={rows} rowKey={(r) => r.key} dense onRowClick={(r) => onMonth(r.i)} />
      </Card>
    </div>
  );
}

export function TaxTab({ idx, pidx }: { idx: Ix; pidx: Ix }) {
  const t = useT();
  const [acting, setActing] = useState(false);
  const vo = sum(VOUT, idx), vi = sum(VIN, idx), due = vo - vi, L = sum(PROF, idx);
  const mx = Math.max(...VOUT, ...VIN) * 1.1;
  const kpis: Kpi[] = [
    mTile(t, t('bod.m.vo'), VOUT, idx, pidx), mTile(t, t('bod.m.vi'), VIN, idx, pidx),
    { k: t(due >= 0 ? 'bod.m.vDue' : 'bod.m.vCarry'), v: f1(Math.abs(due)), u: t('bod.m.unit'), sub: t(due >= 0 ? 'bod.m.vDueS' : 'bod.m.vCarryS'), tone: due >= 0 ? 'warn' : 'ok' },
    { k: t('bod.m.cit'), v: f1(Math.max(0, L) * CIT), u: t('bod.m.unit'), sub: t('bod.m.citS') },
  ];
  const seats: (readonly [string, string])[] = [
    [t('bod.m.s.prep'), '[ACC-TAX] NhungTT'],
    [t('bod.m.s.check'), acting ? t('bod.m.s.acting') : t('bod.m.s.vacant')],
    [t('bod.m.s.approve'), '[BOD-DIR] PHILIP'],
    [t('bod.m.s.auth'), 'PAYMENT-APPROVE'],
    ...(acting ? [[t('bod.m.s.self'), t('bod.m.s.selfV')] as const] : []),
  ];
  const cols: Column<MRow>[] = ([['month', 'c.month', '14%'], ['pi', 't.pi', '13%'], ['vi', 't.vi', '15%'], ['vo', 't.vo', '15%'], ['diff', 't.diff', '15%'], ['src', 't.src', '28%']] as const).map(([key, h, width], c) => ({
    key, header: t(`bod.m.${h}`), width, align: c && c < 5 ? 'right' as const : undefined,
    cell: (r: MRow) => (c === 0 ? <b>{r.cells[0]}</b> : c === 4 ? <b className={r.neg ? s.up : undefined}>{r.cells[c]}</b> : c === 5 ? <span className={r.warn ? s.warnTx : s.mute}>{r.cells[c]}</span> : r.cells[c]),
  }));
  const rows: MRow[] = MON.map((m, i) => ({ key: m, i, neg: VOUT[i]! - VIN[i]! < 0, warn: i >= 7, cells: [m, fi(PI[i]!), f1(VIN[i]!), f1(VOUT[i]!), f1(VOUT[i]! - VIN[i]!), i >= 7 ? t('bod.m.t.sinv') : 'INVOUT'] })).reverse();
  return (
    <div className={s.grid}>
      <Kpis items={kpis} />
      <MChart title={t('bod.m.chartTax')} sub={t('bod.m.byMonth')} idx={idx} mx={mx} series={[VOUT, VIN]} ymax={t('bod.m.peak', { v: f1(mx) })}
        aria={`${t('bod.m.chartTax')}: ${MS.map((m, i) => `${m} ${f1(VOUT[i]!)} / ${f1(VIN[i]!)}`).join(', ')}`}
        legend={[[t('bod.m.vo'), 'Ob'], [t('bod.m.vi'), 'Tr']]} />
      <div className={`${s.span4} ${s.stack}`}>
        <ListPanel title={t('bod.m.seats')} sub={t('bod.m.seatsS')} span={4} rows={seats} />
        <Button variant="secondary" onClick={() => setActing((v) => !v)}>{t(acting ? 'bod.m.s.showVacant' : 'bod.m.s.showActing')}</Button>
      </div>
      <Card title={t('bod.m.taxTbl')} sub={t('bod.m.taxTblS')} span={12}>
        <Table label={t('bod.m.taxTbl')} columns={cols} rows={rows} rowKey={(r) => r.key} dense />
      </Card>
    </div>
  );
}

type Loss = { job: string; cus: string; typ: string; route: string; sale: string; m: number; rev: number; cost: number; note: string };
const LOSS: readonly Loss[] = ([
  ['SE261040322', 'GREENBUD', 'OB', 'VNSGN → KRPUS', 'DAVID', 11, 48.6, 55.0, 'DET trả rỗng trễ · đã ghi KPE-2610-002'],
  ['SE261040115', 'RUITONGVN-SC', 'OB', 'VNSGN → CNNGB', 'DAVID', 11, 31.2, 33.4, 'Phí amend manifest · KPE-2610-001'],
  ['SL261040087', 'TAMNGOC', 'LS', 'Cần Thơ → VNCLI', 'PHILIP', 11, 18.4, 19.9, 'Phí chờ xe tại kho Tam Ngọc Cờ Đỏ'],
  ['SE261040455', 'NAMMY', 'OB', 'VNVUT → JPTYO', 'TUANNT', 11, 42.0, 43.1, 'Late SI · KPE-2610-004'],
  ['SE261040377', 'QUEKYCORP', 'OB', 'VNSGN → SAJED', 'ANDG', 11, 66.5, 69.8, 'Cước mua tăng sau khi chốt giá bán'],
  ['SE260939870', 'PHUCUONGEXIM', 'OB', 'VNSGN → CNSHK', 'DAVID', 10, 27.3, 30.6, 'Chi nhầm đối tác · KPE-2609-014'],
  ['SE260939655', 'ARRIVE', 'OB', 'VNHPH → KRINC', 'TUANNT', 10, 22.8, 23.5, 'Lưu bãi Cát Lái 4 ngày'],
  ['SE260838911', 'COCOGROUP', 'OB', 'VNSGN → MYPKG', 'AnNPH', 9, 19.6, 20.2, 'Phụ thu điện lạnh phát sinh'],
] as const).map(([job, cus, typ, route, sale, m, rev, cost, note]) => ({ job, cus, typ, route, sale, m, rev, cost, note }));

export function LossTab({ idx }: { idx: Ix }) {
  const t = useT();
  const [lot, setLot] = useState<LotFilter>(() => { const q = typeof location === 'undefined' ? null : new URLSearchParams(location.search).get('lot'); return LOT_FILTERS.find((k) => k === q) ?? 'loss'; });
  const toast = useToast();
  const rows = LOSS.filter((r) => idx.includes(r.m));
  const tot = rows.reduce((a, r) => a + (r.cost - r.rev), 0);
  const logged = rows.filter((r) => /KPE-/.test(r.note)).length;
  const kpis: Kpi[] = [
    { k: t('bod.m.l.jobs'), v: fi(rows.length), sub: t('bod.m.l.jobsS') },
    { k: t('bod.m.l.total'), v: f1(-tot), u: t('bod.m.unit'), tone: 'err' },
    { k: t('bod.m.l.avg'), v: rows.length ? f1(-tot / rows.length) : '—', u: rows.length ? t('bod.m.unit') : undefined },
    { k: t('bod.m.l.logged'), v: `${fi(logged)} / ${rows.length}`, sub: t('bod.m.l.loggedS') },
  ];
  const cols: Column<Loss>[] = [
    { key: 'job', header: t('bod.m.l.c.job'), width: '12%', cell: (r) => <b>{r.job}</b> },
    { key: 'cus', header: t('bod.m.l.c.cus'), width: '13%', cell: (r) => r.cus },
    { key: 'typ', header: t('bod.m.l.c.type'), width: '6%', cell: (r) => <Chip tone="info">{r.typ}</Chip> },
    { key: 'route', header: t('bod.m.l.c.route'), width: '13%', cell: (r) => r.route },
    { key: 'sale', header: t('bod.m.l.c.sale'), width: '8%', cell: (r) => r.sale },
    { key: 'rev', header: t('bod.m.c.rev'), width: '9%', align: 'right', cell: (r) => f1(r.rev) },
    { key: 'cost', header: t('bod.m.c.cost'), width: '9%', align: 'right', cell: (r) => f1(r.cost) },
    { key: 'p', header: t('bod.m.c.profit'), width: '9%', align: 'right', cell: (r) => <b className={s.down}>{f1(r.rev - r.cost)}</b> },
    { key: 'note', header: t('bod.m.l.c.note'), width: '13%', cell: (r) => <span className={s.mute}>{r.note}</span> },
    { key: 'act', header: '', width: '8%', align: 'right', cell: (r) => (/KPE-/.test(r.note) ? null : <Button variant="secondary" onClick={() => toast({ msg: t('bod.m.l.logged2', { job: r.job }) })}>{t('bod.m.l.log')}</Button>) },
  ];
  const bar = (
    <div className={s.span12}>
      <Segmented kind="toggle" label={t('lot.pick')} value={lot} onChange={setLot} items={LOT_FILTERS.map((k) => ({ key: k, label: t(`lot.f.${k}`) }))} />
    </div>
  );
  if (lot !== 'loss') return <div className={s.grid}>{bar}<LotView filter={lot} idx={idx} lossLots={LOSS} /></div>;
  return (
    <div className={s.grid}>
      {bar}
      <Kpis items={kpis} />
      {rows.length > 0 && <BarsPanel title={t('bod.m.l.byJob')} sub={t('bod.m.l.byJobS')} span={12} rows={rows.slice().sort((a, b) => (b.cost - b.rev) - (a.cost - a.rev)).map((r) => ({ k: r.job, n: r.cost - r.rev, tone: 'err' as const, v: f1(-(r.cost - r.rev)), s: `${r.cus} · ${r.route}` }))} />}
      <Card title={t('bod.m.l.list')} span={12}>
        {rows.length ? <Table label={t('bod.m.l.list')} columns={cols} rows={rows} rowKey={(r) => r.job} dense /> : <p className={s.mute}>{t('bod.m.l.empty')}</p>}
      </Card>
    </div>
  );
}
